import { useEffect, useState, useCallback } from "react";
import { useApi } from "./AuthContext";
import { toast } from "sonner";
import {
  Search, Save, Globe, Image as ImageIcon, ExternalLink, FileCode,
  Eye, RefreshCw, Building2, Info, Check
} from "lucide-react";

const EMPTY = {
  site_title: "",
  site_description: "",
  keywords: "",
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  og_image_alt: "",
  twitter_card: "summary_large_image",
  twitter_handle: "",
  business_name: "",
  business_phone: "",
  business_address: "",
  business_region: "",
  robots_index: true,
  robots_follow: true,
  robots_extra: "",
  google_review_url: "",
};

function Field({ label, hint, children, count }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-semibold text-stone-700">{label}</span>
        {count != null && <span className="text-xs text-stone-400">{count}</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
    </label>
  );
}

function PreviewCard({ form }) {
  const url = (form.canonical_url || window.location.origin).replace(/^https?:\/\//, "").replace(/\/$/, "");
  const title = form.og_title || form.site_title || "Georgia Outdoor Worx";
  const desc = form.og_description || form.site_description || "";
  const img = form.og_image;

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid="seo-preview-card">
      <div className="aspect-[1.91/1] bg-stone-100 flex items-center justify-center overflow-hidden">
        {img ? (
          <img src={img} alt={form.og_image_alt || ""} className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="text-center text-stone-400 p-6">
            <ImageIcon className="w-10 h-10 mx-auto mb-2" />
            <p className="text-xs">No OG image set — add one for richer social previews (1200×630 ideal)</p>
          </div>
        )}
      </div>
      <div className="p-3 bg-stone-50">
        <div className="text-[11px] uppercase tracking-wide text-stone-500 truncate">{url}</div>
        <div className="text-sm font-semibold text-stone-800 mt-0.5 line-clamp-2">{title}</div>
        <div className="text-xs text-stone-600 mt-0.5 line-clamp-2">{desc}</div>
      </div>
    </div>
  );
}

export default function SeoModule({ readOnly = false }) {
  const api = useApi();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/seo");
      setForm({ ...EMPTY, ...data });
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load SEO settings");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/seo", form);
      toast.success("SEO settings saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const apiBase = window.location.origin;
  const robotsUrl = `${apiBase}/api/robots.txt`;
  const sitemapUrl = `${apiBase}/api/sitemap.xml`;

  if (loading) {
    return <div className="text-center text-stone-500 py-12" data-testid="seo-loading">Loading SEO settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="seo-module">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-forest-700" />
          <h3 className="text-lg font-bold text-stone-800">SEO & Meta Tags</h3>
          {dirty && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Unsaved</span>}
        </div>
        {!readOnly && (
          <button
            data-testid="seo-save-btn"
            onClick={save}
            disabled={saving || !dirty}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold min-h-[44px] ${
              dirty ? "bg-forest-700 text-white hover:bg-forest-800" : "bg-stone-200 text-stone-500 cursor-not-allowed"
            }`}
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: form (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Core meta tags */}
          <section className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-stone-800 font-semibold">
              <Globe className="w-4 h-4 text-forest-700" /> Core Meta Tags
            </div>
            <Field label="Page Title" hint="Shown in browser tab and Google results (50-60 chars ideal)" count={`${(form.site_title || "").length}/60`}>
              <input
                data-testid="seo-input-title"
                type="text"
                value={form.site_title || ""}
                onChange={(e) => update("site_title", e.target.value)}
                disabled={readOnly}
                maxLength={120}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="Meta Description" hint="Shown under the title in Google (150-160 chars ideal)" count={`${(form.site_description || "").length}/160`}>
              <textarea
                data-testid="seo-input-description"
                value={form.site_description || ""}
                onChange={(e) => update("site_description", e.target.value)}
                disabled={readOnly}
                maxLength={320}
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="Keywords" hint="Comma-separated (legacy; helpful for some niche engines)">
              <input
                data-testid="seo-input-keywords"
                type="text"
                value={form.keywords || ""}
                onChange={(e) => update("keywords", e.target.value)}
                disabled={readOnly}
                maxLength={400}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="Canonical URL" hint="Production domain (e.g., https://georgiaoutdoorworx.com). Leave empty to auto-detect.">
              <input
                data-testid="seo-input-canonical"
                type="url"
                value={form.canonical_url || ""}
                onChange={(e) => update("canonical_url", e.target.value)}
                disabled={readOnly}
                placeholder="https://georgiaoutdoorworx.com"
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
          </section>

          {/* Open Graph */}
          <section className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-stone-800 font-semibold">
              <ImageIcon className="w-4 h-4 text-forest-700" /> Open Graph (WhatsApp / Facebook / LinkedIn)
            </div>
            <Field label="OG Title" hint="Defaults to Page Title if empty">
              <input
                data-testid="seo-input-og-title"
                type="text"
                value={form.og_title || ""}
                onChange={(e) => update("og_title", e.target.value)}
                disabled={readOnly}
                maxLength={120}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="OG Description" hint="Defaults to Meta Description if empty">
              <textarea
                data-testid="seo-input-og-description"
                value={form.og_description || ""}
                onChange={(e) => update("og_description", e.target.value)}
                disabled={readOnly}
                maxLength={320}
                rows={2}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="OG Image URL" hint="1200×630 PNG/JPG. Used by WhatsApp, Instagram, Facebook, LinkedIn previews.">
              <input
                data-testid="seo-input-og-image"
                type="url"
                value={form.og_image || ""}
                onChange={(e) => update("og_image", e.target.value)}
                disabled={readOnly}
                placeholder="https://…/social-share.jpg"
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="OG Image Alt Text" hint="Accessibility / fallback when image fails to load">
              <input
                data-testid="seo-input-og-alt"
                type="text"
                value={form.og_image_alt || ""}
                onChange={(e) => update("og_image_alt", e.target.value)}
                disabled={readOnly}
                maxLength={200}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
            <Field label="Twitter Handle" hint="Optional. Example: @gowlandscape">
              <input
                data-testid="seo-input-twitter"
                type="text"
                value={form.twitter_handle || ""}
                onChange={(e) => update("twitter_handle", e.target.value)}
                disabled={readOnly}
                maxLength={60}
                placeholder="@gowlandscape"
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </Field>
          </section>

          {/* Local business */}
          <section className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-stone-800 font-semibold">
              <Building2 className="w-4 h-4 text-forest-700" /> Local Business (Google Maps / Knowledge Panel)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Business Name">
                <input
                  data-testid="seo-input-business-name"
                  type="text"
                  value={form.business_name || ""}
                  onChange={(e) => update("business_name", e.target.value)}
                  disabled={readOnly}
                  maxLength={120}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
              </Field>
              <Field label="Business Phone">
                <input
                  data-testid="seo-input-business-phone"
                  type="tel"
                  value={form.business_phone || ""}
                  onChange={(e) => update("business_phone", e.target.value)}
                  disabled={readOnly}
                  maxLength={40}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
              </Field>
              <Field label="Business Address">
                <input
                  data-testid="seo-input-business-address"
                  type="text"
                  value={form.business_address || ""}
                  onChange={(e) => update("business_address", e.target.value)}
                  disabled={readOnly}
                  maxLength={300}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
              </Field>
              <Field label="Service Region">
                <input
                  data-testid="seo-input-business-region"
                  type="text"
                  value={form.business_region || ""}
                  onChange={(e) => update("business_region", e.target.value)}
                  disabled={readOnly}
                  maxLength={120}
                  placeholder="Georgia, USA"
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
              </Field>
              <Field
                label="Google Review Link"
                hint="Used in invoice emails and the public invoice page. Get the link from your Google Business Profile → 'Get more reviews'."
              >
                <input
                  data-testid="seo-input-google-review-url"
                  type="url"
                  value={form.google_review_url || ""}
                  onChange={(e) => update("google_review_url", e.target.value)}
                  disabled={readOnly}
                  maxLength={400}
                  placeholder="https://g.page/r/.../review"
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
              </Field>
            </div>
          </section>

          {/* Robots / Sitemap */}
          <section className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-stone-800 font-semibold">
              <FileCode className="w-4 h-4 text-forest-700" /> Crawlers (robots.txt &amp; sitemap.xml)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50">
                <input
                  data-testid="seo-input-robots-index"
                  type="checkbox"
                  checked={!!form.robots_index}
                  onChange={(e) => update("robots_index", e.target.checked)}
                  disabled={readOnly}
                  className="w-5 h-5 accent-forest-700"
                />
                <div>
                  <div className="text-sm font-semibold text-stone-800">Allow indexing</div>
                  <div className="text-xs text-stone-500">Let search engines list pages</div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50">
                <input
                  data-testid="seo-input-robots-follow"
                  type="checkbox"
                  checked={!!form.robots_follow}
                  onChange={(e) => update("robots_follow", e.target.checked)}
                  disabled={readOnly}
                  className="w-5 h-5 accent-forest-700"
                />
                <div>
                  <div className="text-sm font-semibold text-stone-800">Allow following links</div>
                  <div className="text-xs text-stone-500">Crawlers traverse internal links</div>
                </div>
              </label>
            </div>
            <Field label="Extra robots.txt rules (advanced)" hint="Appended verbatim to the generated robots.txt">
              <textarea
                data-testid="seo-input-robots-extra"
                value={form.robots_extra || ""}
                onChange={(e) => update("robots_extra", e.target.value)}
                disabled={readOnly}
                maxLength={2000}
                rows={3}
                placeholder="User-agent: GPTBot&#10;Disallow: /"
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 font-mono"
              />
            </Field>
          </section>
        </div>

        {/* Right column: preview + URLs */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 text-stone-800 font-semibold mb-2">
              <Eye className="w-4 h-4 text-forest-700" /> Social Share Preview
            </div>
            <PreviewCard form={form} />
            <p className="text-xs text-stone-500 mt-2 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              How your landing page will look when shared on WhatsApp, Instagram, Facebook, or LinkedIn.
            </p>
          </div>

          <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold text-stone-800">Public Endpoints</div>
            {[
              { label: "robots.txt", url: robotsUrl, testid: "seo-link-robots" },
              { label: "sitemap.xml", url: sitemapUrl, testid: "seo-link-sitemap" },
            ].map((it) => (
              <a
                key={it.label}
                data-testid={it.testid}
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-2 px-3 py-2 border border-stone-200 rounded-md hover:bg-stone-50 text-sm"
              >
                <span className="font-mono text-stone-700">/{it.label}</span>
                <ExternalLink className="w-4 h-4 text-stone-400" />
              </a>
            ))}
            <p className="text-xs text-stone-500 leading-relaxed pt-1">
              The sitemap is regenerated on every request and includes the live portfolio. Submit the sitemap URL to{" "}
              <a className="text-forest-700 underline" href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Google Search Console</a>.
            </p>
          </div>

          <button
            data-testid="seo-reload-btn"
            onClick={load}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-stone-700 bg-white border border-stone-300 hover:bg-stone-50 min-h-[44px]"
          >
            <RefreshCw className="w-4 h-4" /> Reload from server
          </button>
          {!dirty && (
            <div className="text-xs text-stone-500 flex items-center gap-1">
              <Check className="w-3 h-3 text-forest-600" /> All changes saved
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
